import { useState, useRef, useEffect, forwardRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaChevronUp, 
  FaChevronDown, 
  FaClock, 
  FaTimes,
  FaCheck,
  FaSlidersH,
  FaList,
  FaTh
} from "react-icons/fa";
import DropdownPortal from "../common/DropdownPortal";
import { arClass } from "../../utils/useArClass";

// Extracted static texts for different languages
const timeTexts = {
  en: {
    selectTime: "Select time",
    selectedTime: "Selected time",
    hours: "Hours",
    minutes: "Minutes",
    am: "AM",
    pm: "PM",
    clear: "Clear",
    apply: "Apply",
    close: "Close",
    now: "Now",
    hourLabel: "Hour",
    minuteLabel: "Minute",
    timePicker: "Time Picker",
    timeSelection: "Time Selection",
    quickSelection: "Quick Selection",
    navTimePicker: "Picker",
    navSelection: "Selection",
    navQuick: "Quick Pick",
    timeFormat: "Time Format",
    hour12: "12-hour",
    hour24: "24-hour"
  },
  fr: {
    selectTime: "Sélectionner l'heure",
    selectedTime: "Heure sélectionnée",
    hours: "Heures",
    minutes: "Minutes",
    am: "AM",
    pm: "PM",
    clear: "Effacer",
    apply: "Appliquer",
    close: "Fermer",
    now: "Maintenant",
    hourLabel: "Heure",
    minuteLabel: "Minute",
    timePicker: "Sélecteur d'heure",
    timeSelection: "Sélection d'heure",
    quickSelection: "Sélection rapide",
    navTimePicker: "Sélecteur",
    navSelection: "Sélection",
    navQuick: "Rapide",
    timeFormat: "Format horaire",
    hour12: "12-heures",
    hour24: "24-heures"
  },
  ar: {
    selectTime: "اختر الوقت",
    selectedTime: "الوقت المختار",
    hours: "ساعات",
    minutes: "دقائق",
    am: "ص",
    pm: "م",
    clear: "مسح",
    apply: "تطبيق",
    close: "إغلاق",
    now: "الآن",
    hourLabel: "ساعة",
    minuteLabel: "دقيقة",
    timePicker: "منتقي الوقت",
    timeSelection: "اختيار الوقت",
    quickSelection: "اختيار سريع",
    navTimePicker: "المنتقي",
    navSelection: "الاختيار",
    navQuick: "سريع",
    timeFormat: "تنسيق الوقت",
    hour12: "١٢ ساعة",
    hour24: "٢٤ ساعة"
  }
};

const TimeInput = forwardRef(({ 
  value = "",
  onChange,
  name,
  className = "",
  placeholder = null,
  minTime = null,
  maxTime = null,
  disabled = false,
  readOnly = false,
  showNowButton = true,
  allowClear = true,
  step = 1,
  use12Hour = false,
  register = null,
  registerProp = "time",
  onBlur,
  ...props
}, ref) => {
  const defaultLanguage = useSelector((state) => state.settings.defaultLanguage) || "en";
  const darkMode = useSelector((state) => state.settings.darkMode);
  
  const texts = timeTexts[defaultLanguage] || timeTexts.en;
  const isRTL = defaultLanguage === "ar";
  
  const parseTimeValue = (timeString) => {
    if (!timeString) return { hours: 0, minutes: 0, period: "AM" };
    
    if (timeString.includes(":")) {
      const [hours, minutes] = timeString.split(":");
      let hourNum = parseInt(hours, 10);
      let period = "AM";
      
      if (use12Hour) {
        if (hourNum >= 12) {
          period = "PM";
          if (hourNum > 12) hourNum -= 12;
        }
        if (hourNum === 0) hourNum = 12;
      }
      
      return {
        hours: hourNum,
        minutes: parseInt(minutes, 10),
        period,
        originalHours: parseInt(hours, 10)
      };
    }
    
    return { hours: 0, minutes: 0, period: "AM" };
  };
  
  const formatTimeForDisplay = (hours, minutes, period = null) => {
    if (hours === null || minutes === null) return "";
    
    const date = new Date();
    let displayHours = hours;
    
    if (use12Hour && period) {
      if (period === "PM" && hours < 12) displayHours += 12;
      if (period === "AM" && hours === 12) displayHours = 0;
    }
    
    date.setHours(displayHours);
    date.setMinutes(minutes);
    
    return date.toLocaleTimeString(
      defaultLanguage === "ar" ? "ar-MA" :
      defaultLanguage === "fr" ? "fr-FR" :
      "en-US",
      { 
        hour: "2-digit", 
        minute: "2-digit",
        hour12: use12Hour
      }
    );
  };
  
  const formatTimeForValue = (hours, minutes, period = null) => {
    let valueHours = hours;
    
    if (use12Hour && period) {
      if (period === "PM" && hours < 12) valueHours += 12;
      if (period === "AM" && hours === 12) valueHours = 0;
    }
    
    return `${String(valueHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };
  
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState(parseTimeValue(value));
  const [tempHours, setTempHours] = useState(selectedTime.hours);
  const [tempMinutes, setTempMinutes] = useState(selectedTime.minutes);
  const [tempPeriod, setTempPeriod] = useState(selectedTime.period);
  const [activeTab, setActiveTab] = useState("picker"); // "picker", "selection", "quick"
  const [tempUse12Hour, setTempUse12Hour] = useState(use12Hour);
  const [selectionSubTab, setSelectionSubTab] = useState("hours");
  const hoursScrollRef = useRef(null);

  const lastScrollTimeRef = useRef(0);
  const hoursPrevScrollRef = useRef(0);
  const minutesPrevScrollRef = useRef(0);
  const [editingField, setEditingField] = useState(null); // 'hours' | 'minutes' | null
  const [editingValue, setEditingValue] = useState("");
  const editingInputRef = useRef(null);
  
  const buttonRef = useRef(null);
  const autoRepeatTimeoutRef = useRef(null);
  const autoRepeatIntervalRef = useRef(null);
  
  // Combine refs
  const combinedRef = useRef(null);
  useEffect(() => {
    if (typeof ref === 'function') {
      ref(combinedRef.current);
    } else if (ref) {
      ref.current = combinedRef.current;
    }
  }, [ref]);

  // cleanup auto-repeat timers on unmount
  useEffect(() => {
    return () => {
      if (autoRepeatTimeoutRef.current) clearTimeout(autoRepeatTimeoutRef.current);
      if (autoRepeatIntervalRef.current) clearInterval(autoRepeatIntervalRef.current);
      autoRepeatTimeoutRef.current = null;
      autoRepeatIntervalRef.current = null;
    };
  }, []);

  // focus editing input when entering edit mode
  useEffect(() => {
    if (editingField && editingInputRef.current) {
      try { editingInputRef.current.focus(); editingInputRef.current.select(); } catch (e) {}
    }
  }, [editingField]);
  
  // Initialize with register if provided
  const registerProps = register ? register(registerProp, {
    value: value,
    onChange: (e) => {
      const parsed = parseTimeValue(e.target.value);
      setSelectedTime(parsed);
      setTempHours(parsed.hours);
      setTempMinutes(parsed.minutes);
      setTempPeriod(parsed.period);
    }
  }) : {};
  
  // Update when value prop changes
  useEffect(() => {
    if (value) {
      const parsed = parseTimeValue(value);
      setSelectedTime(parsed);
      setTempHours(parsed.hours);
      setTempMinutes(parsed.minutes);
      setTempPeriod(parsed.period);
    } else {
      setSelectedTime({ hours: 0, minutes: 0, period: "AM" });
      setTempHours(0);
      setTempMinutes(0);
      setTempPeriod("AM");
    }
    setTempUse12Hour(use12Hour);
  }, [value, use12Hour]);
  
  // Generate hour options
  const hourOptions = useMemo(() => {
    const hours = [];
    const maxHour = tempUse12Hour ? 12 : 23;
    
    for (let i = tempUse12Hour ? 1 : 0; i <= maxHour; i++) {
      hours.push(i);
    }
    
    return hours;
  }, [tempUse12Hour]);
  
  // Generate minute options based on step
  const minuteOptions = useMemo(() => {
    const minutes = [];
    const maxMinutes = 59;
    
    for (let i = 0; i <= maxMinutes; i += step) {
      minutes.push(i);
    }
    
    if (step > 1 && !minutes.includes(59)) {
      minutes.push(59);
    }
    
    return minutes;
  }, [step]);

  const handleGridWheel = (type, e) => {
    e.preventDefault();
    const now = Date.now();
    if (now - lastScrollTimeRef.current < 110) return;
    lastScrollTimeRef.current = now;
    if (e.deltaY < 0) {
      if (type === "hours") incrementHours();
      else incrementMinutes();
    } else if (e.deltaY > 0) {
      if (type === "hours") decrementHours();
      else decrementMinutes();
    }
  };

  const handleGridScroll = (type, e) => {
    const now = Date.now();
    if (now - lastScrollTimeRef.current < 110) return;
    const target = e.target;
    const prev = type === "hours" ? hoursPrevScrollRef : minutesPrevScrollRef;
    const diff = target.scrollTop - prev.current;
    if (Math.abs(diff) < 20) return;
    lastScrollTimeRef.current = now;
    prev.current = target.scrollTop;
    if (diff < 0) {
      if (type === "hours") incrementHours();
      else incrementMinutes();
    } else {
      if (type === "hours") decrementHours();
      else decrementMinutes();
    }
  };
  
  // Generate preset times
  const presetTimes = useMemo(() => {
    const presets = [];
    const intervals = [
      "07:00", "08:00", "10:00", "12:00", "14:00", "15:00", "16:00", "18:00", "20:00"
    ];
    
    intervals.forEach(time => {
      const [h, m] = time.split(":").map(Number);
      let displayHour = h;
      let period = "AM";
      
      if (tempUse12Hour) {
        if (h >= 12) {
          period = "PM";
          if (h > 12) displayHour = h - 12;
        }
        if (h === 0) displayHour = 12;
      }
      
      presets.push({
        value: time,
        display: formatTimeForDisplay(displayHour, m, period),
        hours: displayHour,
        minutes: m,
        period
      });
    });
    
    return presets;
  }, [tempUse12Hour, step]);
  
  const isTimeValid = (hours, minutes) => {
    if (hours === null || minutes === null) return false;
    if (hours < (tempUse12Hour ? 1 : 0) || hours > (tempUse12Hour ? 12 : 23)) return false;
    if (minutes < 0 || minutes > 59) return false;
    
    if (minTime || maxTime) {
      const timeValue = formatTimeForValue(hours, minutes, tempPeriod);
      const timeMinutes = hours * 60 + minutes;
      
      if (minTime) {
        const [minH, minM] = minTime.split(":").map(Number);
        const minMinutes = minH * 60 + minM;
        if (timeMinutes < minMinutes) return false;
      }
      
      if (maxTime) {
        const [maxH, maxM] = maxTime.split(":").map(Number);
        const maxMinutes = maxH * 60 + maxM;
        if (timeMinutes > maxMinutes) return false;
      }
    }
    
    return true;
  };
  
  const handleTimeSelect = () => {
    if (!isTimeValid(tempHours, tempMinutes)) return;
    
    const newTime = {
      hours: tempHours,
      minutes: tempMinutes,
      period: tempPeriod
    };
    
    setSelectedTime(newTime);
    const formattedValue = formatTimeForValue(tempHours, tempMinutes, tempPeriod);
    
    if (onChange && typeof onChange === "function") {
      onChange({ target: { name, value: formattedValue, type: "time" } });
    }
    
    if (onBlur && typeof onBlur === "function") {
      onBlur({ target: { name, value: formattedValue } });
    }
    
    setIsOpen(false);
  };
  
  const setToNow = () => {
    const now = new Date();
    let hours = now.getHours();
    const minutes = Math.floor(now.getMinutes() / step) * step;
    let period = "AM";
    
    if (tempUse12Hour) {
      period = hours >= 12 ? "PM" : "AM";
      if (hours > 12) hours -= 12;
      if (hours === 0) hours = 12;
    }
    
    setTempHours(hours);
    setTempMinutes(minutes);
    setTempPeriod(period);
  };
  
  const clearTime = (e) => {
    if (e) e.stopPropagation();
    
    setSelectedTime({ hours: 0, minutes: 0, period: "AM" });
    setTempHours(0);
    setTempMinutes(0);
    setTempPeriod("AM");
    
    const formattedValue = "";
    
    if (onChange && typeof onChange === "function") {
      onChange({ target: { name, value: formattedValue, type: "time" } });
    }
    
    if (onBlur && typeof onBlur === "function") {
      onBlur({ target: { name, value: formattedValue } });
    }
  };
  
  const incrementHours = () => {
    let newHours = tempHours + 1;
    if (tempUse12Hour && newHours > 12) newHours = 1;
    if (!tempUse12Hour && newHours > 23) newHours = 0;
    setTempHours(newHours);
  };
  
  const decrementHours = () => {
    let newHours = tempHours - 1;
    if (tempUse12Hour && newHours < 1) newHours = 12;
    if (!tempUse12Hour && newHours < 0) newHours = 23;
    setTempHours(newHours);
  };
  
  const incrementMinutes = () => {
    let newMinutes = tempMinutes + step;
    if (newMinutes > 59) {
      newMinutes = 0;
      incrementHours();
    }
    setTempMinutes(newMinutes);
  };
  
  const decrementMinutes = () => {
    let newMinutes = tempMinutes - step;
    if (newMinutes < 0) {
      newMinutes = 60 - step;
      decrementHours();
    }
    setTempMinutes(newMinutes);
  };
  
  const togglePeriod = () => {
    setTempPeriod(tempPeriod === "AM" ? "PM" : "AM");
  };
  
  const toggleTimeFormat = () => {
    const newFormat = !tempUse12Hour;
    setTempUse12Hour(newFormat);
    
    // Convert current time to new format
    if (tempHours !== 0 || tempMinutes !== 0) {
      let convertedHours = tempHours;
      let convertedPeriod = tempPeriod;
      
      if (newFormat) {
        // Convert from 24h to 12h
        if (tempHours === 0) {
          convertedHours = 12;
          convertedPeriod = "AM";
        } else if (tempHours === 12) {
          convertedHours = 12;
          convertedPeriod = "PM";
        } else if (tempHours > 12) {
          convertedHours = tempHours - 12;
          convertedPeriod = "PM";
        } else {
          convertedPeriod = "AM";
        }
      } else {
        // Convert from 12h to 24h
        if (tempPeriod === "PM" && tempHours < 12) {
          convertedHours = tempHours + 12;
        } else if (tempPeriod === "AM" && tempHours === 12) {
          convertedHours = 0;
        }
      }
      
      setTempHours(convertedHours);
      setTempPeriod(convertedPeriod);
    }
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95, y: -10 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: { 
        type: "spring",
        stiffness: 300,
        damping: 25
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.95, 
      y: -10,
      transition: { duration: 0.2 }
    }
  };
  
  const tabContentVariants = {
    hidden: { opacity: 0, x: isRTL ? 20 : -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.3, ease: "easeInOut" }
    },
    exit: { 
      opacity: 0, 
      x: isRTL ? -20 : 20,
      transition: { duration: 0.2 }
    }
  };
  
  const timeDisplayVariants = {
    initial: { scale: 1 },
    pulse: { 
      scale: [1, 1.05, 1],
      transition: { duration: 0.3 }
    }
  };
  
  const buttonVariants = {
    hover: { scale: 1.05 },
    tap: { scale: 0.95 }
  };
  
  // Get color scheme
  const getColorScheme = () => {
    const entry = {
      light: {
        bg: "!bg-white text-gray-900",
        color: "!text-gray-900",
        subBg: "!bg-gray-50/90 !backdrop-blur-sm",
        border: "!border-gray-300",
        hover: "hover:!bg-gray-200",
        active: "!bg-gray-200 !text-gray-900 !border-gray-400 !shadow",
        iconBg: "!bg-gray-300",
        glow: "!shadow-lg !shadow-gray-300/20",
      },
      dark: {
        bg: "!bg-gray-600 !text-gray-50",
        color: "!text-gray-50",
        subBg: "!bg-gray-700/90 !backdrop-blur-sm",
        border: "!border-gray-300",
        hover: "hover:!bg-gray-800",
        active: "!bg-gray-600 !text-white !border-gray-500 shadow",
        iconBg: "!bg-gray-600",
        glow: "!shadow-lg !shadow-gray-700/30",
      },
    };

    return darkMode ? entry.dark : entry.light;
  };
  
  const colors = getColorScheme();
  
  // RTL styling
  const rtlClasses = isRTL ? "rtl text-right" : "";
  const flexDirectionClass = isRTL ? "flex-row-reverse" : "";
  const textAlignClass = isRTL ? "text-right" : "text-left";
  
  // Hidden input for React Hook Form integration
  const hiddenInputProps = register ? registerProps : {
    value: value,
    onChange: (e) => {
      const parsed = parseTimeValue(e.target.value);
      setSelectedTime(parsed);
      setTempHours(parsed.hours);
      setTempMinutes(parsed.minutes);
      setTempPeriod(parsed.period);
    },
    onBlur: onBlur,
    name: name,
    ...props
  };

  // Format display text
  const displayText = selectedTime.hours === 0 && selectedTime.minutes === 0 
    ? (placeholder || texts.selectTime)
    : formatTimeForDisplay(selectedTime.hours, selectedTime.minutes, selectedTime.period);

  return (
    <div className={`relative w-full ${rtlClasses}`}>
      {/* Hidden input for form submission */}
      <input
        type="hidden"
        {...hiddenInputProps}
        ref={combinedRef}
      />
      
      <motion.div
        type="button"
        ref={buttonRef}
        onClick={() => !disabled && !readOnly && setIsOpen(!isOpen)}
        disabled={disabled}
        className={arClass(`w-full hover:!transform-none flex items-center justify-between gap-3 p-3 rounded-md text-sm font-medium transition-all duration-300 max-sm:text-xs max-sm:p-2 border ${
          darkMode 
            ? "bg-gray-700 text-gray-100 border-gray-600 hover:bg-gray-600" 
            : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className} `)}
        whileHover={disabled ? {} : { scale: 1.01 }}
        whileTap={disabled ? {} : { scale: 0.99 }}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            if (onBlur && typeof onBlur === "function") {
              onBlur({
                target: {
                  name,
                  value: selectedTime.hours === 0 && selectedTime.minutes === 0 
                    ? "" 
                    : formatTimeForValue(selectedTime.hours, selectedTime.minutes, selectedTime.period)
                }
              });
            }
          }
        }}
      >
        <div className={`flex items-center gap-2 ${flexDirectionClass}`}>
          <FaClock className={darkMode ? "text-gray-400" : "text-gray-500"} />
          <span className={`max-sm:text-xs ${textAlignClass}`}>
            {displayText}
          </span>
        </div>
        <div className={`flex items-center gap-2 ${flexDirectionClass}`}>
          {allowClear && (selectedTime.hours !== 0 || selectedTime.minutes !== 0) && (
            <motion.button
              type="button"
              onClick={clearTime}
              className={`p-1 rounded ${darkMode ? "hover:bg-gray-600" : "hover:bg-gray-200"}`}
              title={texts.clear}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaTimes className="w-3 h-3" />
            </motion.button>
          )}
        </div>
      </motion.div>
      
      <AnimatePresence>
        {isOpen && !readOnly && (
          <DropdownPortal
            anchorRef={buttonRef}
            onClose={() => setIsOpen(false)}
            className={`${colors.subBg} border ${colors.border} shadow-lg rounded-md ${textAlignClass} w-full z-50 min-w-72`}
            position="bottom"
          >
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="w-full"
            >
              {/* Header with animated time display */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-600">
                <motion.div 
                  className={`text-center ${flexDirectionClass}`}
                  variants={timeDisplayVariants}
                  initial="initial"
                  animate="pulse"
                >
                  <div className={`text-3xl font-bold mb-1 ${darkMode ? "text-white" : "text-gray-800"}`}>
                    {formatTimeForDisplay(tempHours, tempMinutes, tempPeriod)}
                  </div>
                  <div className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {texts.selectedTime}
                  </div>
                </motion.div>
              </div>
              
              {/* Navigation Tabs */}
              <div className={`flex border-b border-gray-200 dark:border-gray-600 ${flexDirectionClass}`}>
                {[
                  { id: "picker", label: texts.navTimePicker, icon: FaSlidersH },
                  { id: "selection", label: texts.navSelection, icon: FaList },
                  { id: "quick", label: texts.navQuick, icon: FaTh }
                ].map((tab) => (
                  <motion.button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? darkMode
                          ? "bg-gray-600 text-white border-b-2 border-blue-500"
                          : "bg-gray-100 text-blue-600 border-b-2 border-blue-500"
                        : darkMode
                        ? "text-gray-400 hover:text-gray-200 hover:bg-gray-700"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <tab.icon className="w-3.5 h-3.5" />
                    <span className="text-xs">{tab.label}</span>
                  </motion.button>
                ))}
              </div>
              
              {/* Tab Content */}
              <div className="p-4">
                <AnimatePresence mode="wait">
                  {activeTab === "picker" && (
                    <motion.div
                      key="picker"
                      variants={tabContentVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className="space-y-4"
                    >
                      <div className={arClass(`text-xs font-medium mb-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                        {texts.timePicker}
                      </div>
                      
                      {/* Time Format Toggle */}
                      <div className={arClass(`flex items-center justify-between mb-4 ${flexDirectionClass}`)}>
                        <span className={arClass(`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`)}>
                          {texts.timeFormat}
                        </span>
                        <motion.button
                          type="button"
                          onClick={toggleTimeFormat}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full text-xs ${
                            tempUse12Hour
                              ? darkMode ? "bg-blue-600" : "bg-blue-500"
                              : darkMode ? "bg-gray-600" : "bg-gray-300"
                          }`}
                          whileTap={{ scale: 0.95 }}
                        >
                          <motion.span
                            className={`inline-block h-4 w-4 rounded-full bg-white`}
                            animate={{ x: tempUse12Hour ? (isRTL ? 6 : 22) : (isRTL ? 22 : 6) }}
                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                          />
                        </motion.button>
                        <span className={`text-xs font-medium ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                          {tempUse12Hour ? texts.hour12 : texts.hour24}
                        </span>
                      </div>
                      
                      {/* Time Selection Controls */}
                      <div className={arClass(`flex items-center justify-center gap-4 ${flexDirectionClass}`)}>
                        {/* Hours */}
                        <div className="flex flex-col items-center">
                          <div className={`text-xs mb-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                            {texts.hours}
                          </div>
                          <div className="flex flex-col items-center gap-2">
                            <motion.button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                incrementHours();
                                autoRepeatTimeoutRef.current = setTimeout(() => {
                                  autoRepeatIntervalRef.current = setInterval(incrementHours, 120);
                                }, 500);
                              }}
                              onMouseUp={() => {
                                clearTimeout(autoRepeatTimeoutRef.current);
                                clearInterval(autoRepeatIntervalRef.current);
                                autoRepeatTimeoutRef.current = null;
                                autoRepeatIntervalRef.current = null;
                              }}
                              onMouseLeave={() => {
                                clearTimeout(autoRepeatTimeoutRef.current);
                                clearInterval(autoRepeatIntervalRef.current);
                                autoRepeatTimeoutRef.current = null;
                                autoRepeatIntervalRef.current = null;
                              }}
                              onTouchStart={(e) => {
                                e.preventDefault();
                                incrementHours();
                                autoRepeatTimeoutRef.current = setTimeout(() => {
                                  autoRepeatIntervalRef.current = setInterval(incrementHours, 120);
                                }, 500);
                              }}
                              onTouchEnd={() => {
                                clearTimeout(autoRepeatTimeoutRef.current);
                                clearInterval(autoRepeatIntervalRef.current);
                                autoRepeatTimeoutRef.current = null;
                                autoRepeatIntervalRef.current = null;
                              }}
                              className={`p-3 rounded-full max-sm:p-2 text-xs ${
                                darkMode 
                                  ? "bg-gray-600 hover:bg-gray-500" 
                                  : "bg-gray-200 hover:bg-gray-300"
                              }`}
                              variants={buttonVariants}
                              whileHover="hover"
                              whileTap="tap"
                            >
                              <FaChevronUp className="w-3.5 h-3.5" />
                            </motion.button>
                            <motion.div 
                              className={`text-sm font-bold w-16 text-center py-3 max-sm:p-2 rounded-lg ${
                                darkMode ? "bg-gray-800" : "bg-gray-100"
                              }`}
                              whileHover={{ scale: 1.05 }}
                              onWheel={(e) => {
                                e.preventDefault();
                                if (e.deltaY < 0) incrementHours();
                                else if (e.deltaY > 0) decrementHours();
                              }}
                              onClick={() => {
                                setEditingField("hours");
                                setEditingValue(String(tempHours));
                              }}
                            >
                              {editingField === "hours" ? (
                                <input
                                  ref={editingInputRef}
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  className={`w-full text-center bg-transparent outline-none ${darkMode ? "text-white" : "text-gray-900"}`}
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value.replace(/[^0-9]/g, ""))}
                                  onBlur={() => {
                                    const num = parseInt(editingValue || "0", 10);
                                    const min = tempUse12Hour ? 1 : 0;
                                    const max = tempUse12Hour ? 12 : 23;
                                    const clamped = Number.isNaN(num) ? min : Math.max(min, Math.min(max, num));
                                    setTempHours(clamped);
                                    setEditingField(null);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      const num = parseInt(editingValue || "0", 10);
                                      const min = tempUse12Hour ? 1 : 0;
                                      const max = tempUse12Hour ? 12 : 23;
                                      const clamped = Number.isNaN(num) ? min : Math.max(min, Math.min(max, num));
                                      setTempHours(clamped);
                                      setEditingField(null);
                                    } else if (e.key === "Escape") {
                                      setEditingField(null);
                                    }
                                  }}
                                />
                              ) : (
                                String(tempHours).padStart(2, "0")
                              )}
                            </motion.div>
                            <motion.button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                decrementHours();
                                autoRepeatTimeoutRef.current = setTimeout(() => {
                                  autoRepeatIntervalRef.current = setInterval(decrementHours, 120);
                                }, 500);
                              }}
                              onMouseUp={() => {
                                clearTimeout(autoRepeatTimeoutRef.current);
                                clearInterval(autoRepeatIntervalRef.current);
                                autoRepeatTimeoutRef.current = null;
                                autoRepeatIntervalRef.current = null;
                              }}
                              onMouseLeave={() => {
                                clearTimeout(autoRepeatTimeoutRef.current);
                                clearInterval(autoRepeatIntervalRef.current);
                                autoRepeatTimeoutRef.current = null;
                                autoRepeatIntervalRef.current = null;
                              }}
                              onTouchStart={(e) => {
                                e.preventDefault();
                                decrementHours();
                                autoRepeatTimeoutRef.current = setTimeout(() => {
                                  autoRepeatIntervalRef.current = setInterval(decrementHours, 120);
                                }, 500);
                              }}
                              onTouchEnd={() => {
                                clearTimeout(autoRepeatTimeoutRef.current);
                                clearInterval(autoRepeatIntervalRef.current);
                                autoRepeatTimeoutRef.current = null;
                                autoRepeatIntervalRef.current = null;
                              }}
                              className={`p-3 rounded-full max-sm:p-2 text-xs ${
                                darkMode 
                                  ? "bg-gray-600 hover:bg-gray-500" 
                                  : "bg-gray-200 hover:bg-gray-300"
                              }`}
                              variants={buttonVariants}
                              whileHover="hover"
                              whileTap="tap"
                            >
                              <FaChevronDown className="w-3.5 h-3.5" />
                            </motion.button>
                          </div>
                        </div>
                        
                        <div className={`text-sm font-bold mt-8 ${darkMode ? "text-white" : "text-gray-800"}`}>
                          :
                        </div>
                        
                        {/* Minutes */}
                        <div className="flex flex-col items-center">
                          <div className={`text-xs mb-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                            {texts.minutes}
                          </div>
                          <div className="flex flex-col items-center gap-2">
                            <motion.button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                incrementMinutes();
                                autoRepeatTimeoutRef.current = setTimeout(() => {
                                  autoRepeatIntervalRef.current = setInterval(incrementMinutes, 120);
                                }, 500);
                              }}
                              onMouseUp={() => {
                                clearTimeout(autoRepeatTimeoutRef.current);
                                clearInterval(autoRepeatIntervalRef.current);
                                autoRepeatTimeoutRef.current = null;
                                autoRepeatIntervalRef.current = null;
                              }}
                              onMouseLeave={() => {
                                clearTimeout(autoRepeatTimeoutRef.current);
                                clearInterval(autoRepeatIntervalRef.current);
                                autoRepeatTimeoutRef.current = null;
                                autoRepeatIntervalRef.current = null;
                              }}
                              onTouchStart={(e) => {
                                e.preventDefault();
                                incrementMinutes();
                                autoRepeatTimeoutRef.current = setTimeout(() => {
                                  autoRepeatIntervalRef.current = setInterval(incrementMinutes, 120);
                                }, 500);
                              }}
                              onTouchEnd={() => {
                                clearTimeout(autoRepeatTimeoutRef.current);
                                clearInterval(autoRepeatIntervalRef.current);
                                autoRepeatTimeoutRef.current = null;
                                autoRepeatIntervalRef.current = null;
                              }}
                              className={`p-3 rounded-full max-sm:p-2 text-xs ${
                                darkMode 
                                  ? "bg-gray-600 hover:bg-gray-500" 
                                  : "bg-gray-200 hover:bg-gray-300"
                              }`}
                              variants={buttonVariants}
                              whileHover="hover"
                              whileTap="tap"
                            >
                              <FaChevronUp className="w-3.5 h-3.5" />
                            </motion.button>
                            <motion.div 
                              className={`text-sm font-bold w-16 text-center py-3 rounded-lg max-sm:p-2  ${
                                darkMode ? "bg-gray-800" : "bg-gray-100"
                              }`}
                              whileHover={{ scale: 1.05 }}
                              onWheel={(e) => {
                                e.preventDefault();
                                if (e.deltaY < 0) incrementMinutes();
                                else if (e.deltaY > 0) decrementMinutes();
                              }}
                              onClick={() => {
                                setEditingField("minutes");
                                setEditingValue(String(tempMinutes));
                              }}
                            >
                              {editingField === "minutes" ? (
                                <input
                                  ref={editingInputRef}
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  className={`w-full text-center bg-transparent outline-none ${darkMode ? "text-white" : "text-gray-900"}`}
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value.replace(/[^0-9]/g, ""))}
                                  onBlur={() => {
                                    const num = parseInt(editingValue || "0", 10);
                                    const clamped = Number.isNaN(num) ? 0 : Math.max(0, Math.min(59, num));
                                    // align to step
                                    const stepped = Math.min(59, Math.round(clamped / step) * step);
                                    setTempMinutes(stepped);
                                    setEditingField(null);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      const num = parseInt(editingValue || "0", 10);
                                      const clamped = Number.isNaN(num) ? 0 : Math.max(0, Math.min(59, num));
                                      const stepped = Math.min(59, Math.round(clamped / step) * step);
                                      setTempMinutes(stepped);
                                      setEditingField(null);
                                    } else if (e.key === "Escape") {
                                      setEditingField(null);
                                    }
                                  }}
                                />
                              ) : (
                                String(tempMinutes).padStart(2, "0")
                              )}
                            </motion.div>
                            <motion.button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                decrementMinutes();
                                autoRepeatTimeoutRef.current = setTimeout(() => {
                                  autoRepeatIntervalRef.current = setInterval(decrementMinutes, 120);
                                }, 500);
                              }}
                              onMouseUp={() => {
                                clearTimeout(autoRepeatTimeoutRef.current);
                                clearInterval(autoRepeatIntervalRef.current);
                                autoRepeatTimeoutRef.current = null;
                                autoRepeatIntervalRef.current = null;
                              }}
                              onMouseLeave={() => {
                                clearTimeout(autoRepeatTimeoutRef.current);
                                clearInterval(autoRepeatIntervalRef.current);
                                autoRepeatTimeoutRef.current = null;
                                autoRepeatIntervalRef.current = null;
                              }}
                              onTouchStart={(e) => {
                                e.preventDefault();
                                decrementMinutes();
                                autoRepeatTimeoutRef.current = setTimeout(() => {
                                  autoRepeatIntervalRef.current = setInterval(decrementMinutes, 120);
                                }, 500);
                              }}
                              onTouchEnd={() => {
                                clearTimeout(autoRepeatTimeoutRef.current);
                                clearInterval(autoRepeatIntervalRef.current);
                                autoRepeatTimeoutRef.current = null;
                                autoRepeatIntervalRef.current = null;
                              }}
                              className={`p-3 rounded-full max-sm:p-2 text-xs ${
                                darkMode 
                                  ? "bg-gray-600 hover:bg-gray-500" 
                                  : "bg-gray-200 hover:bg-gray-300"
                              }`}
                              variants={buttonVariants}
                              whileHover="hover"
                              whileTap="tap"
                            >
                              <FaChevronDown className="w-3.5 h-3.5" />
                            </motion.button>
                          </div>
                        </div>
                        
                        {/* AM/PM Toggle */}
                        {tempUse12Hour && (
                          <div className="flex flex-col items-center">
                            <div className={`text-xs mb-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                              {texts.am}/{texts.pm}
                            </div>
                            <div className="flex flex-col items-center gap-2">
                              <motion.button
                                type="button"
                                onClick={togglePeriod}
                                className={`p-3 rounded-full ${
                                  darkMode 
                                    ? "bg-gray-600 hover:bg-gray-500" 
                                    : "bg-gray-200 hover:bg-gray-300"
                                }`}
                                variants={buttonVariants}
                                whileHover="hover"
                                whileTap="tap"
                              >
                                <FaChevronUp className="w-3.5 h-3.5" />
                              </motion.button>
                              <motion.div 
                                className={`text-sm font-bold w-12 text-center py-3 rounded-lg ${
                                  darkMode ? "bg-gray-800" : "bg-gray-100"
                                }`}
                                whileHover={{ scale: 1.05 }}
                              >
                                {tempPeriod}
                              </motion.div>
                              <motion.button
                                type="button"
                                onClick={togglePeriod}
                                className={`p-3 rounded-full ${
                                  darkMode 
                                    ? "bg-gray-600 hover:bg-gray-500" 
                                    : "bg-gray-200 hover:bg-gray-300"
                                }`}
                                variants={buttonVariants}
                                whileHover="hover"
                                whileTap="tap"
                              >
                                <FaChevronDown className="w-3.5 h-3.5" />
                              </motion.button>
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                  
                  {activeTab === "selection" && (
                    <motion.div
                      key="selection"
                      variants={tabContentVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className="space-y-4"
                    >
                      <div className={`text-xs font-medium mb-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                        {texts.timeSelection}
                      </div>

                      {/* Selection nav: choose Hours or Minutes */}
                      <div className="flex gap-2 mb-3">
                        <button
                          type="button"
                          onClick={() => setSelectionSubTab("hours")}
                          className={`px-3 py-2 rounded text-xs max-sm:p-2 ${selectionSubTab === "hours" ? (darkMode ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-600") : (darkMode ? "bg-gray-700 text-gray-200" : "bg-white text-gray-700")} `}
                        >
                          {texts.hours}
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectionSubTab("minutes")}
                          className={`px-3 py-2 rounded text-xs max-sm:p-2  ${selectionSubTab === "minutes" ? (darkMode ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-600") : (darkMode ? "bg-gray-700 text-gray-200" : "bg-white text-gray-700")} `}
                        >
                          {texts.minutes}
                        </button>
                      </div>

                      <div className="mb-6">
                        {/* Content: show either hours or minutes; desktop uses grid, mobile uses scroll list */}
                        {selectionSubTab === "hours" ? (
                          <>
                            <div className={`text-xs mb-3 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                              {texts.hours}:
                            </div>

                            <div className="hidden sm:grid grid-cols-6 gap-1">
                              {hourOptions.map((hour) => (
                                <motion.button
                                  key={hour}
                                  type="button"
                                  onClick={() => setTempHours(hour)}
                                  className={`p-2 rounded max-sm:p-1 text-xs ${
                                    tempHours === hour
                                      ? darkMode
                                        ? "bg-blue-600 text-white"
                                        : "bg-blue-500 text-white"
                                      : darkMode
                                      ? "hover:bg-gray-600"
                                      : "hover:bg-gray-200"
                                  }`}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  {String(hour).padStart(2, "0")}
                                </motion.button>
                              ))}
                            </div>

                            <div className="sm:hidden">
                              <div
                                ref={hoursScrollRef}
                                onWheel={(e) => handleGridWheel("hours", e)}
                                onScroll={(e) => handleGridScroll("hours", e)}
                                className="max-h-48 overflow-y-auto space-y-1"
                              >
                                {hourOptions.map((hour) => (
                                  <button
                                    key={hour}
                                    type="button"
                                    onClick={() => setTempHours(hour)}
                                    className={`w-full text-left px-3 py-2 rounded ${tempHours === hour ? (darkMode ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-600") : (darkMode ? "hover:bg-gray-700 text-gray-200" : "hover:bg-gray-50 text-gray-700")}`}
                                  >
                                    {String(hour).padStart(2, "0")}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className={`text-xs mb-3 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                              {texts.minutes}:
                            </div>

                            <div className="grid grid-cols-8 gap-1 max-h-36 overflow-y-auto">
                              {minuteOptions.map((minute) => (
                                <motion.button
                                  key={minute}
                                  type="button"
                                  onClick={() => setTempMinutes(minute)}
                                  className={`p-2 rounded max-sm:p-1 text-xs ${
                                    tempMinutes === minute
                                      ? darkMode
                                        ? "bg-blue-600 text-white"
                                        : "bg-blue-500 text-white"
                                      : darkMode
                                      ? "hover:bg-gray-600"
                                      : "hover:bg-gray-200"
                                  }`}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  {String(minute).padStart(2, "0")}
                                </motion.button>
                              ))}
                            </div>

                          </>
                        )}
                      </div>
                    </motion.div>
                  )}
                  
                  {activeTab === "quick" && (
                    <motion.div
                      key="quick"
                      variants={tabContentVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className="space-y-4"
                    >
                      <div className={`text-xs font-medium mb-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                        {texts.quickSelection}
                      </div>
                      
                      <div className="grid grid-cols-3 gap-1">
                        {presetTimes.map((preset) => (
                          <motion.button
                            key={preset.value}
                            type="button"
                            onClick={() => {
                              setTempHours(preset.hours);
                              setTempMinutes(preset.minutes);
                              setTempPeriod(preset.period);
                            }}
                            className={`p-2 rounded max-sm:p-1 text-xs ${
                              tempHours === preset.hours && tempMinutes === preset.minutes
                                ? darkMode
                                  ? "bg-blue-600 text-white"
                                  : "bg-blue-500 text-white"
                                : darkMode
                                ? "hover:bg-gray-600"
                                : "hover:bg-gray-200"
                            }`}
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                          >
                            {preset.display}
                          </motion.button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Footer */}
              <div className={`flex items-center justify-between p-4 border-t ${darkMode ? "border-gray-600" : "border-gray-200"} ${flexDirectionClass}`}>
                {showNowButton && (
                  <motion.button
                    type="button"
                    onClick={setToNow}
                    className={`px-4 py-2 rounded max-sm:p-2 text-xs ${
                      darkMode
                        ? "bg-gray-600 hover:bg-gray-500"
                        : "bg-gray-200 hover:bg-gray-300"
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {texts.now}
                  </motion.button>
                )}
                
                <div className={`flex items-center gap-2 ${flexDirectionClass}`}>
                  <motion.button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className={`px-4 py-2 rounded max-sm:p-2 text-xs ${
                      darkMode
                        ? "bg-gray-600 hover:bg-gray-500"
                        : "bg-gray-200 hover:bg-gray-300"
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {texts.close}
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={handleTimeSelect}
                    disabled={!isTimeValid(tempHours, tempMinutes)}
                    className={`px-4 py-2 rounded max-sm:p-2 text-xs ${
                      !isTimeValid(tempHours, tempMinutes)
                        ? darkMode
                          ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : darkMode
                        ? "bg-blue-600 hover:bg-blue-500 text-white"
                        : "bg-blue-500 hover:bg-blue-600 text-white"
                    }`}
                    whileHover={isTimeValid(tempHours, tempMinutes) ? { scale: 1.05 } : {}}
                    whileTap={isTimeValid(tempHours, tempMinutes) ? { scale: 0.95 } : {}}
                  >
                    {texts.apply}
                  </motion.button>
                </div>
              </div>
              
              {/* Time Constraints Warning */}
              {(minTime || maxTime) && !isTimeValid(tempHours, tempMinutes) && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mx-4 mb-4 p-3 rounded text-xs ${
                    darkMode ? "bg-red-900/30 text-red-300" : "bg-red-50 text-red-600"
                  }`}
                >
                  {minTime && maxTime 
                    ? `Time must be between ${minTime} and ${maxTime}`
                    : minTime 
                    ? `Time must be after ${minTime}`
                    : `Time must be before ${maxTime}`
                  }
                </motion.div>
              )}
            </motion.div>
          </DropdownPortal>
        )}
      </AnimatePresence>
    </div>
  );
});

TimeInput.displayName = "TimeInput";

export default TimeInput;